import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Overlay,
  ModalContainer,
  ModalContent,
  Title,
  Subtitle,
  Label,
  Input,
  PasswordWrapper,
  GenerateButton,
  ButtonGroup,
  CancelButton,
  ShareButton,
  PasswordRow,
  PasswordInput,
  BackButton,
  SpanTitle,
  StyledDatePicker,
  PasswordIcons,
  HorizondalLine,
  AccessListSection,
  RevokeLink,
  SubLabel,
  ErrorText,
  ErrorIconWrapper,
  FolderPicker,
  FolderRow,
  PickerNote,
  LinkPanel,
  LinkText,
  LinkRow,
  LinkMeta,
} from "./ShareFileModalStyled";
import { ArrowsClockwise, Copy, Eye, EyeSlash, X } from "@phosphor-icons/react";
import { DatePickerDropdownStyles } from "../DatePicker";
import dayjs from "dayjs";
import { dateFormatList } from "../../utils/constants";
import ConfirmModal from "../ConfirmModal/ConfirmModal";
import { useDispatch, useSelector } from "react-redux";
import { getGeneratedPassword } from "../../slice/MatterDetailsSlice";
import {
  fetchFolderExternalShareDetails,
  revokeFolderExternalShare,
  resetFolderShareDetails,
  submitFolderExternalShare,
} from "../../slice/ExternalFolderShareSlice";
import { ErrorIcon, WarningIcon } from "../../assets/icons";
import { validateExternalShare } from "../../utils/ESignModalValidation";
import Loader from "../Loader/Loader";
import { MessageModal } from "../common/Modals";
import {
  buildFolderRows,
  foldersToSend,
  impliedByAncestor,
} from "./folderPickerRows";

// Fork of ShareFileModal.js for sharing folders (and their live subtrees)
// instead of a single document. The link this creates is LIVE: the
// recipient always sees whatever is currently in the folders, not a
// snapshot — so, unlike the document share, there is nothing here to
// "protect" ahead of time. A password is an access gate on the landing
// page, not per-file encryption; see documentation/features/external_sharing.md.
//
// Matthew, 9/9, asked this screen for two more things: several folders on
// one link, and the link on screen rather than only in an email. Both
// changed the shape of the dialog — it no longer flips into a read-only
// "already shared" state, because a file usually needs sending to more
// than one outside doctor and that state left no way to make a second
// link.
const ShareFolderModal = ({ isOpen, onClose, folder_id, handleSnackbar }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [expiryDate, setExpiryDate] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [createdLink, setCreatedLink] = useState(null);
  const [errors, setErrors] = useState({
    email: "",
    password: "",
    expiryDate: "",
  });
  const [isLoading, setisLoading] = useState(false);

  const dispatch = useDispatch();
  const { folderShareDetails } = useSelector((state) => state.externalFolderShare);
  const isExternalShareEnabled = useSelector(
    (state) => state.featureFlags.isExternalShareEnabled
  );

  const rows = useMemo(
    () => buildFolderRows(folderShareDetails?.pickable_folders),
    [folderShareDetails?.pickable_folders]
  );
  const implied = useMemo(
    () => impliedByAncestor(rows, selectedIds),
    [rows, selectedIds]
  );
  const liveLinks = folderShareDetails?.links || [];

  // Closing does not unmount this dialog (`if (!isOpen) return null`),
  // so Done/Cancel would otherwise leave the last URL and extra ticks
  // sitting there for the next folder you share.
  useEffect(() => {
    setCreatedLink(null);
    setEmail("");
    setPassword("");
    setExpiryDate(null);
    setShowPassword(false);
    setShowRevokeModal(false);
    setErrors({ email: "", password: "", expiryDate: "" });
    setSelectedIds(folder_id ? new Set([folder_id]) : new Set());
    if (isOpen && folder_id) {
      dispatch(fetchFolderExternalShareDetails({ folder_id })).catch(() => {});
    }
  }, [isOpen, folder_id, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(resetFolderShareDetails());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getFolderExternalShareDetails = () => {
    dispatch(fetchFolderExternalShareDetails({ folder_id })).catch(() => {});
  };

  const generatePassword = () => {
    dispatch(getGeneratedPassword())
      .then((response) => {
        setPassword(response?.payload?.data?.password);
      })
      .catch(() => {});
  };

  const toggleFolder = (id) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const sending = foldersToSend(rows, selectedIds);

  const handleSubmit = () => {
    const { externalShareErrors, hasError } = validateExternalShare({
      email,
      password,
      expiryDate,
      // Every new link sets its own password and expiry, so the old
      // "an active share exists, skip these" branch no longer applies.
      accessList: [],
      // Blank means link only: it is created and handed back to be copied.
      emailOptional: true,
    });

    setErrors(externalShareErrors);

    if (hasError) return;
    setisLoading(true);

    const payload = {
      folders: sending,
      recipient_emails: email,
      password,
      expires_at: dayjs(expiryDate).format("YYYY-MM-DDTHH:mm:ss.SSS"),
    };

    dispatch(submitFolderExternalShare(payload))
      .unwrap()
      .then((response) => {
        // The dialog stays open holding the link. Closing on success was
        // fine while the only delivery was an email; now the link is the
        // delivery, and closing would lose it.
        setCreatedLink(response?.data?.share_link || null);
        handleSnackbar(
          email
            ? `Shared, and the link was emailed to ${email}`
            : "Link created",
          true,
          false
        );
        setEmail("");
        setPassword("");
        setExpiryDate(null);
        getFolderExternalShareDetails();
      })
      .catch(() => {
        handleSnackbar("Something went wrong", false, false);
      })
      .finally(() => {
        setisLoading(false);
      });
  };

  if (!isOpen) return null;

  const copyToClipboard = (value, label) => {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        handleSnackbar(`${label} copied to clipboard`, true);
      })
      .catch(() => {
        handleSnackbar(`Failed to copy ${label.toLowerCase()}`);
      });
  };

  const handleRevoke = () => {
    dispatch(revokeFolderExternalShare({ folder_id }))
      .then((response) => {
        if (response?.payload?.status === 200) {
          handleSnackbar(response?.payload?.data?.message, true, false);
          setShowRevokeModal(false);
          onClose();
        } else {
          handleSnackbar("Failed to revoke share", false, false);
        }
      })
      .catch(() => {
        handleSnackbar("Something went wrong", false, false);
      });
  };

  const describeRecipients = (link) =>
    link.recipient_list?.length
      ? link.recipient_list.join(", ")
      : "Link only — not emailed";

  return (
    <Overlay>
      {isExternalShareEnabled ? (
        <ModalContainer>
          <ModalContent>
            <Title>
              Share Folders Externally -{" "}
              <SpanTitle>{folderShareDetails?.foldername}</SpanTitle>
            </Title>
            <Subtitle>
              People with the link can view and download everything currently
              in the folders you tick, including subfolders. New files show up
              automatically; deleted files disappear.
            </Subtitle>

            {createdLink && (
              <LinkPanel>
                <PasswordRow>
                  <Label small>Your link</Label>
                  <GenerateButton
                    onClick={() => copyToClipboard(createdLink, "Link")}
                  >
                    <Copy size={16} color="#0C53BB" />
                    Copy Link
                  </GenerateButton>
                </PasswordRow>
                <LinkText>{createdLink}</LinkText>
              </LinkPanel>
            )}

            <SubLabel>Which folders?</SubLabel>
            <FolderPicker>
              {rows.map((row) => {
                const isImplied = implied.has(row.id);
                return (
                  <FolderRow key={row.id} depth={row.depth}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(row.id) || isImplied}
                      disabled={isImplied}
                      onChange={() => toggleFolder(row.id)}
                      aria-label={row.name}
                    />
                    {row.name}
                    {isImplied && " (inside a ticked folder)"}
                  </FolderRow>
                );
              })}
            </FolderPicker>
            <PickerNote>
              {sending.length === 1
                ? "1 folder on this link."
                : `${sending.length} folders on this link.`}
            </PickerNote>

            <Label small>Email it to someone (optional)</Label>
            <Input
              type="email"
              placeholder="Enter email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={Boolean(errors.email)}
            />
            {errors.email && (
              <ErrorText>
                <ErrorIcon />
                {errors.email}
              </ErrorText>
            )}

            <SubLabel>Security</SubLabel>
            <PasswordRow>
              <Label small>Set Password (Optional)</Label>
              <GenerateButton onClick={generatePassword}>
                <ArrowsClockwise size={16} color="#0C53BB" />
                Generate
              </GenerateButton>
            </PasswordRow>
            <PasswordWrapper>
              <PasswordInput
                type={showPassword ? "text" : "password"}
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={Boolean(errors.password)}
              />
              {/* Both controls, not one or the other. A password you have
                  typed or generated is the one you most need to read back
                  before sending it on. */}
              <PasswordIcons>
                <span
                  role="button"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <Eye size={16} /> : <EyeSlash size={16} />}
                </span>
                {password && (
                  <span
                    role="button"
                    aria-label="Copy password"
                    onClick={() => copyToClipboard(password, "Password")}
                  >
                    <Copy size={16} color="#0C53BB" />
                  </span>
                )}
              </PasswordIcons>
            </PasswordWrapper>
            {errors.password && (
              <ErrorText style={{ alignItems: "flex-start" }}>
                <ErrorIconWrapper>
                  <ErrorIcon />
                </ErrorIconWrapper>
                {errors.password}
              </ErrorText>
            )}

            <Label small>Folder Expiry Date</Label>
            <>
              <DatePickerDropdownStyles />
              <StyledDatePicker
                format={dateFormatList}
                getPopupContainer={() => document.querySelector(".share-modal")}
                className={errors.expiryDate ? "error" : ""}
                onChange={(date) => setExpiryDate(date)}
                allowClear={false}
                inputReadOnly
                placeholder="Due Date"
                value={expiryDate}
                disabledDate={(current) => {
                  const today = dayjs().startOf("day");
                  // 90 days, matching what the folder-share endpoint
                  // accepts. Matthew's call on 9/9: a doctor's opinion
                  // routinely takes longer than a month, and a link that
                  // dies mid-opinion means the request starts again.
                  const maxDate = dayjs().add(90, "day").endOf("day");
                  return current && (current < today || current > maxDate);
                }}
                error={Boolean(errors.expiryDate)}
              />
              {errors.expiryDate && (
                <ErrorText>
                  <ErrorIcon />
                  {errors.expiryDate}
                </ErrorText>
              )}
            </>

            {liveLinks.length > 0 && (
              <AccessListSection>
                <PasswordRow>
                  <SubLabel>Links to this file</SubLabel>
                  <RevokeLink onClick={() => setShowRevokeModal(true)}>
                    Revoke Access
                  </RevokeLink>
                </PasswordRow>
                {liveLinks.map((link) => (
                  <LinkRow key={link.id}>
                    <LinkMeta>
                      <strong>
                        {link.folders?.map((f) => f.name).join(", ")}
                      </strong>
                      {describeRecipients(link)}
                      {" · expires "}
                      {dayjs(link.expires_at).format("MM/DD/YYYY")}
                      {link.has_password && " · password set"}
                    </LinkMeta>
                    <GenerateButton
                      onClick={() => copyToClipboard(link.share_link, "Link")}
                    >
                      <Copy size={16} color="#0C53BB" />
                      Copy Link
                    </GenerateButton>
                  </LinkRow>
                ))}
              </AccessListSection>
            )}

            <HorizondalLine />
            <ButtonGroup>
              <CancelButton onClick={onClose}>
                {createdLink ? "Done" : "Cancel"}
              </CancelButton>
              <ShareButton
                onClick={handleSubmit}
                disabled={!(sending.length > 0 && expiryDate)}
              >
                {createdLink ? "Create Another Link" : "Share"}
              </ShareButton>
            </ButtonGroup>
          </ModalContent>
          <BackButton>
            <X size={20} onClick={onClose} />
          </BackButton>
        </ModalContainer>
      ) : (
        <MessageModal
          open={true}
          onClose={onClose}
          title={"Warning: External Share Disabled"}
          message={
            "External Share Email notifications are currently disabled. Please contact your admin."
          }
          buttonText={"Close"}
          icon={<WarningIcon />}
          onClick={onClose}
          variant="warning"
        />
      )}
      <ConfirmModal
        isOpen={showRevokeModal}
        title="Revoke Access?"
        message="This will disable every link to this folder for all recipients."
        onCancel={() => setShowRevokeModal(false)}
        onConfirm={handleRevoke}
      />
      {isLoading && <Loader message={"Sharing folders..."} />}
    </Overlay>
  );
};

ShareFolderModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  folder_id: PropTypes.string.isRequired,
  handleSnackbar: PropTypes.func,
};

export default ShareFolderModal;
